// src/index.ts
import * as Yup from "yup";
Yup.setLocale({
  mixed: {
    required: "${path} darf nicht leer sein"
  },
  string: {
    min: "${path} muss mindestens ${min} Zeichen lang sein"
  }
});
var CurrencyRegex = /\d+\.\d{2}/;
var CurrencyField = Yup.string().matches(
  CurrencyRegex,
  "${path} muss ein valider monet\xE4rer Betrag sein"
);
var CustomerDisplayNameField = Yup.string().label("Anzeigename").min(2).required();
var CustomerPrintNameField = Yup.string().label("Druckname");
var CustomerBilledHourlyRateField = CurrencyField.test(
  "divide_by_four",
  "${path} muss durch vier teilbar sein",
  function divideByFourValidator(value) {
    if (value === null || value === void 0) {
      return true;
    }
    if (typeof value === "string") {
      const lastTwoCharacters = value.slice(-2);
      const afterTheComma = parseInt(lastTwoCharacters, 10);
      return afterTheComma % 4 === 0;
    }
    return false;
  }
).label("Stundensatz");
var CustomerReferenceField = Yup.string().trim().label("Kundenreferenz");
var CustomerNoticeField = Yup.string().trim().label("Kundenreferenz");
var CustomerEmailField = Yup.string().trim().label("E-Mail Adresse");
var CustomerBillingAddress = Yup.string().trim().label("Rechnungsadresse");
var CustomerCreateBackendSchema = Yup.object().shape({
  displayName: CustomerDisplayNameField,
  printName: CustomerPrintNameField,
  reference: CustomerReferenceField,
  billingAddress: CustomerBillingAddress,
  billedHourlyRate: CustomerBilledHourlyRateField,
  importReference: Yup.string().trim(),
  email: CustomerEmailField
});
var CustomerCreateSchema = Yup.object().shape({
  displayName: CustomerDisplayNameField,
  printName: CustomerPrintNameField,
  reference: CustomerReferenceField,
  billingAddress: CustomerBillingAddress,
  billedHourlyRate: CustomerBilledHourlyRateField,
  workItem: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  journeyItem: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  category: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  extraBillingItems: Yup.array(
    Yup.object().shape({
      item: Yup.object().shape({
        id: Yup.string().required()
      }).required(),
      amount: Yup.number().required()
    })
  ),
  phoneNumbers: Yup.array(
    Yup.object().shape({
      phoneNumberId: Yup.string().trim().nullable(),
      remove: Yup.boolean(),
      displayName: Yup.string().trim(),
      // TODO: libphonenumber
      phoneNumber: Yup.string().trim().required()
    })
  ),
  importReference: Yup.string().trim(),
  email: CustomerEmailField
});
var CustomerEditSchema = Yup.object().shape({
  displayName: CustomerDisplayNameField,
  printName: CustomerPrintNameField.min(2).required(),
  reference: CustomerReferenceField,
  billingAddress: CustomerBillingAddress,
  billedHourlyRate: CustomerBilledHourlyRateField.required(),
  workItem: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  journeyItem: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  category: Yup.object().shape({
    id: Yup.string().required()
  }).nullable(),
  extraBillingItems: Yup.array(
    Yup.object().shape({
      item: Yup.object().shape({
        id: Yup.string().required()
      }).required(),
      amount: Yup.number().required()
    })
  ),
  phoneNumbers: Yup.array(
    Yup.object().shape({
      phoneNumberId: Yup.string().trim().nullable(),
      remove: Yup.boolean(),
      displayName: Yup.string().trim(),
      // TODO: libphonenumber
      phoneNumber: Yup.string().trim().required()
    })
  ),
  email: CustomerEmailField,
  notice: CustomerNoticeField
});
var EmployeeDisplayNameField = Yup.string().label("Anzeigename").min(2).required();
var EmployeePrintNameField = Yup.string().label("Druckname").min(2).required();
var EmployeeCreateSchema = Yup.object().shape({
  displayName: EmployeeDisplayNameField,
  printName: EmployeePrintNameField,
  defaultPaidHourlyRate: CurrencyField.label("Standard Stundensatz").required().nullable()
});
var ItemTitleField = Yup.string().label("Name").min(2).required();
var ItemNumberField = Yup.string().label("Artikelnummer").min(1).required();
var ItemCreateSchema = Yup.object().shape({
  title: ItemTitleField,
  itemNumber: ItemNumberField,
  usableForWork: Yup.boolean().required(),
  usableForJourney: Yup.boolean().required(),
  usableForExtraBilling: Yup.boolean().required(),
  usableForManual: Yup.boolean().required()
});
var ItemEditSchema = Yup.object().shape({
  title: ItemTitleField,
  itemNumber: ItemNumberField,
  usableForWork: Yup.boolean().required(),
  usableForJourney: Yup.boolean().required(),
  usableForExtraBilling: Yup.boolean().required(),
  usableForManual: Yup.boolean().required()
});
var CustomerCategoryCreateSchema = Yup.object().shape({
  title: Yup.string().label("Titel").required(),
  description: Yup.string().label("Beschreibung"),
  color: Yup.string().label("Farbe").matches(/^(#([A-Fa-f0-9]{6})|#([A-Fa-f0-9]{3})|transparent)$/)
});
var TimeTrackTitleBlacklist = ["ts", "ti", "preinstall"];
var TimeTrackTitleField = Yup.string().label("Titel").transform((value) => value.trim() === "" ? null : value).min(6).nullable().test(
  "blacklist",
  "${path} darf den Begriff ${keyword} aus der Blacklist nicht enthalten",
  function blacklistValidator(value) {
    if (value !== void 0 && value !== null && typeof value === "string") {
      const words = value.split(/\s/g);
      const blacklistedWords = words.filter(
        (word) => TimeTrackTitleBlacklist.includes(word)
      );
      if (blacklistedWords.length === 1) {
        return this.createError({
          message: `\${path} darf den Begriff "${blacklistedWords[0]}" aus der Blacklist nicht enthalten`
        });
      } else if (blacklistedWords.length > 1) {
        return this.createError({
          message: "${path} darf keine Begriffe aus der Blacklist enthalten"
        });
      }
    }
    return true;
  }
);
var TimeTrackTitle = TimeTrackTitleField;
var TimeTrackNote = Yup.string().nullable();
var TimeTrackSchema = Yup.object().shape({
  title: TimeTrackTitle,
  note: TimeTrackNote
});
var UsernameField = Yup.string().lowercase().trim().matches(
  /^(?!me$|all$).*/,
  "Darf nicht gleich einem reservierten Benutzernamen sein"
);
var DisplayName = Yup.string().trim();
var EmailField = Yup.string().trim().min(1, "Muss eine valide E-Mail sein").matches(/@/, "Muss eine valide E-Mail sein");
var UserSetEmailSchema = Yup.object().shape({
  email: EmailField.required()
});
var UserPasswordMinLength = 8;
var UserPasswordSchema = Yup.string().min(UserPasswordMinLength, "Muss mindestens ${min} Zeichen lang sein").required("Darf nicht leer sein");
var UserCreateSchema = Yup.object().shape({
  displayName: DisplayName.required(),
  username: UsernameField.required(),
  email: EmailField
});
var LiteCallStartSchema = Yup.object().shape({
  internalPhoneNumber: Yup.string().required(),
  externalPhoneNumber: Yup.string().required(),
  isOutbound: Yup.boolean().required(),
  created: Yup.date().required()
});
var LiteCallConnectSchema = Yup.object().shape({
  internalPhoneNumber: Yup.string().required(),
  externalPhoneNumber: Yup.string().required(),
  isOutbound: Yup.boolean().required(),
  connected: Yup.date().required()
});
var LiteCallEndSchema = Yup.object().shape({
  internalPhoneNumber: Yup.string().required(),
  externalPhoneNumber: Yup.string().required(),
  isOutbound: Yup.boolean().required(),
  ended: Yup.date().required()
});
export {
  CurrencyField,
  CurrencyRegex,
  CustomerBilledHourlyRateField,
  CustomerBillingAddress,
  CustomerCategoryCreateSchema,
  CustomerCreateBackendSchema,
  CustomerCreateSchema,
  CustomerDisplayNameField,
  CustomerEditSchema,
  CustomerEmailField,
  CustomerNoticeField,
  CustomerPrintNameField,
  CustomerReferenceField,
  DisplayName,
  EmailField,
  EmployeeCreateSchema,
  EmployeeDisplayNameField,
  EmployeePrintNameField,
  ItemCreateSchema,
  ItemEditSchema,
  ItemNumberField,
  ItemTitleField,
  LiteCallConnectSchema,
  LiteCallEndSchema,
  LiteCallStartSchema,
  TimeTrackNote,
  TimeTrackSchema,
  TimeTrackTitle,
  TimeTrackTitleField,
  UserCreateSchema,
  UserPasswordMinLength,
  UserPasswordSchema,
  UserSetEmailSchema,
  UsernameField
};
//# sourceMappingURL=index.mjs.map